

(function () {

  if (window.__locationInventoryLoaded) return;
  window.__locationInventoryLoaded = true;

  const LI_STORAGE_KEY_COORDS   = 'li_user_coords';
  const LI_STORAGE_KEY_LOCATION = 'li_selected_location';
  const LI_TOP_LOCATIONS        = 3;
  const LI_COORDS_TTL_MS        = 1000 * 60 * 30; 

  function haversineDistanceKm(lat1, lng1, lat2, lng2) {
    const R     = 6371;
    const toRad = (deg) => (deg * Math.PI) / 180;
    const dLat  = toRad(lat2 - lat1);
    const dLng  = toRad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  // ─── GeoService ───────────────────────────

  class GeoService {

    static async getUserCoords() {
      const cached = GeoService._getCached();
      if (cached) return cached;

      try {
        const coords = await GeoService._fromBrowser();
        GeoService._saveToCache(coords);
        return coords;
      } catch {
        try {
          const coords = await GeoService._fromIP();
          GeoService._saveToCache(coords);
          return coords;
        } catch {
          return null;
        }
      }
    }

    static _getCached() {
      try {
        const raw = localStorage.getItem(LI_STORAGE_KEY_COORDS);
        if (!raw) return null;
        const { lat, lng, timestamp } = JSON.parse(raw);
        if (Date.now() - timestamp > LI_COORDS_TTL_MS) return null;
        return { lat, lng };
      } catch {
        return null;
      }
    }

    static _saveToCache(coords) {
      try {
        localStorage.setItem(
          LI_STORAGE_KEY_COORDS,
          JSON.stringify({ ...coords, timestamp: Date.now() })
        );
      } catch {}
    }

    static _fromBrowser() {
      return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error('Geolocation not supported'));
          return;
        }
        navigator.geolocation.getCurrentPosition(
          ({ coords }) => resolve({ lat: coords.latitude, lng: coords.longitude }),
          () => reject(new Error('Geolocation denied')),
          { timeout: 8000 }
        );
      });
    }

    static async _fromIP() {
      const response = await fetch('https://ipapi.co/json/');
      if (!response.ok) throw new Error('IP geolocation failed');
      const data = await response.json();
      return { lat: data.latitude, lng: data.longitude };
    }

    // Solo retorna coords si el permiso ya fue concedido previamente.
    // No dispara el dialog del navegador (para uso en widgets de producto).
    static async getCoordsIfGranted() {
      const cached = GeoService._getCached();
      if (cached) return cached;

      if (!navigator.permissions) return null;
      try {
        const status = await navigator.permissions.query({ name: 'geolocation' });
        if (status.state !== 'granted') return null;
      } catch {
        return null;
      }

      try {
        const coords = await GeoService._fromBrowser();
        GeoService._saveToCache(coords);
        return coords;
      } catch {
        return null;
      }
    }
  }

  // ─── SectionService ───────
  
  class SectionService {
    constructor(rootUrl) {
      this.rootUrl = rootUrl.endsWith('/') ? rootUrl : rootUrl + '/';
    }

    async fetchVariantAvailability(variantId) {
      const domEl = document.getElementById(`li-data-${variantId}`);
      if (domEl) {
        try {
          const data = JSON.parse(domEl.textContent);
          if (data.locations && data.locations.length > 0) return data;
          // Embedded JSON has empty locations — fall through to section fetch
        } catch {}
      }

      const url      = `${this.rootUrl}variants/${variantId}/?section_id=location-inventory`;
      const response = await fetch(url);

      if (!response.ok) throw new Error('Section fetch failed');

      const text    = await response.text();
      const html    = new DOMParser().parseFromString(text, 'text/html');
      const section = html.querySelector('.shopify-section');

      if (!section) return { variantAvailable: false, locations: [] };

      const dataEl = section.querySelector('#location-inventory-data');
      if (!dataEl)  return { variantAvailable: false, locations: [] };

      return JSON.parse(dataEl.textContent);
    }
  }

  // ─── Custom Element: <location-inventory> ────────────────────────────────────

  if (!customElements.get('location-inventory')) {
    customElements.define(
      'location-inventory',
      class LocationInventory extends HTMLElement {

        connectedCallback() {
          this.variantId     = this.dataset.variantId;
          this.compact       = this.hasAttribute('data-compact');
          this.expandable    = this.hasAttribute('data-expandable');
          this._expanded     = false;
          this.maxDistanceKm = this.dataset.maxDistance ? parseFloat(this.dataset.maxDistance) : null;
          this.section       = new SectionService(this.dataset.rootUrl);

          this._variantUnsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const newVariantId = String(event?.data?.variant?.id);
            if (newVariantId && newVariantId !== this.variantId) {
              this.variantId = newVariantId;
              this.refresh();
            }
          });

          this._locationUnsubscribe = subscribe('location-inventory:location-changed', () => {
            this.refresh();
          });

          if (this.compact || this.expandable) {
            this._observer = new IntersectionObserver((entries) => {
              if (!entries[0].isIntersecting) return;
              this._observer.disconnect();
              this.refresh();
            }, { rootMargin: '0px 0px 200px 0px' });
            this._observer.observe(this);
          } else {
            this.refresh();
          }
        }

        disconnectedCallback() {
          if (this._variantUnsubscribe)  this._variantUnsubscribe();
          if (this._locationUnsubscribe) this._locationUnsubscribe();
          if (this._observer)            this._observer.disconnect();
        }

        async refresh() {
          this._setLoading(true);

          try {
            const [userCoords, { variantAvailable, locations }] = await Promise.all([
              GeoService.getCoordsIfGranted(),
              this.section.fetchVariantAvailability(this.variantId),
            ]);

            const withDistance = userCoords
              ? locations
                  .filter((l) => l.lat !== null && l.lng !== null)
                  .map((location) => ({
                    ...location,
                    distanceKm: haversineDistanceKm(
                      userCoords.lat, userCoords.lng,
                      location.lat,  location.lng
                    ),
                  }))
                  .sort((a, b) => a.distanceKm - b.distanceKm)
              : [];

            const withinLimit = this.maxDistanceKm !== null
              ? withDistance.filter((l) => l.distanceKm <= this.maxDistanceKm)
              : withDistance;

            // Priority: within limit → any with distance → all locations (no distance)
            let candidates = withinLimit.length > 0
              ? withinLimit
              : withDistance.length > 0
                ? withDistance
                : locations.map((l) => ({ ...l, distanceKm: null }));

            let selectedId = this._getSelectedLocationId();

            // Si no hay sucursal guardada, aplicar "25 Asunción" como default
            // (cubre el caso de primera visita o cuando el picker aún no corrió)
            if (!selectedId && locations.length > 0) {
              const def = locations.find(
                (l) => String(l.id) === '81185505590'
              );
              if (def) {
                selectedId = String(def.id);
                try { localStorage.setItem(LI_STORAGE_KEY_LOCATION, selectedId); } catch {}
                try { localStorage.setItem('li_location_auto', '1'); } catch {}
                if (typeof publish === 'function') {
                  publish('location-inventory:location-changed', { locationId: selectedId });
                }
              }
            }

            if (selectedId) {
              const selIdx = candidates.findIndex((l) => String(l.id) === String(selectedId));
              if (selIdx > 0) {
                // Already in candidates but not first — move to front
                const sel = candidates[selIdx];
                candidates = [sel, ...candidates.filter((_, i) => i !== selIdx)];
              } else if (selIdx === -1) {
                // Not in candidates — find in all locations and prepend
                const selectedLoc = locations.find((l) => String(l.id) === String(selectedId));
                if (selectedLoc) candidates = [{ ...selectedLoc, distanceKm: null }, ...candidates];
              }
            }

            const sorted = candidates.slice(0, LI_TOP_LOCATIONS);

            if (this.expandable) {
              this._renderExpandable({ sorted, all: candidates, variantAvailable, selectedId });
            } else if (this.compact) {
              this._renderCompact({ sorted, variantAvailable, selectedId });
            } else {
              this._render({ sorted: candidates, variantAvailable, selectedId });
            }

          } catch (error) {
            console.error('[LocationInventory]', error);
            this._renderError();
          } finally {
            this._setLoading(false);
          }
        }

        _render({ sorted, variantAvailable, selectedId }) {
          if (!sorted.length) {
            this.innerHTML = '';
            return;
          }

          const fmt = (km) => km == null ? '' : km < 1
            ? `${Math.round(km * 1000)} m`
            : `${km.toFixed(1)} km`;

          const isManualSelection = selectedId && sorted.some((l) => String(l.id) === String(selectedId));
          const primary = isManualSelection
            ? sorted.find((l) => String(l.id) === String(selectedId))
            : sorted[0];
          const others = sorted.filter((l) => l.id !== primary.id);

          const primaryLabel = isManualSelection ? 'Tu sucursal seleccionada' : 'Tu tienda más cercana';

          const renderCard = (loc, isPrimary, selectable = false) => {
            const dist     = fmt(loc.distanceKm);
            const leadTime = loc.pickUpTime && loc.pickUpTime !== 'null' ? loc.pickUpTime : null;

            const canPickUp = loc.pickUpEnabled !== false && loc.available;

            const pickupPill = `
              <div class="li-compact__item li-compact__item--${canPickUp ? 'available' : 'unavailable'}">
                <span class="li-compact__icon-box li-compact__icon-box--pickup"></span>
                <span class="li-compact__info">
                  <span class="li-compact__name">Retiro en tienda${canPickUp && leadTime ? ` · ${leadTime}` : ''}</span>
                  <span class="li-compact__status">${canPickUp ? 'Disponible para retiro' : 'Sin stock para retiro'}</span>
                </span>
              </div>`;

            const localPill = `
              <div class="li-compact__item li-compact__item--${canPickUp ? 'available' : 'unavailable'}">
                <span class="li-compact__icon-box li-compact__icon-box--local"></span>
                <span class="li-compact__info">
                  <span class="li-compact__name">Entrega local</span>
                  <span class="li-compact__status">${canPickUp ? 'Disponible' : 'Sin stock'}</span>
                </span>
              </div>`;

            const shippingPill = variantAvailable
              ? `<div class="li-compact__item li-compact__item--available">
                   <span class="li-compact__icon-box li-compact__icon-box--delivery"></span>
                   <span class="li-compact__info">
                     <span class="li-compact__name">Envío a domicilio</span>
                     <span class="li-compact__status">Disponible</span>
                   </span>
                 </div>`
              : '';

            const servicesHtml = `${pickupPill}${localPill}${shippingPill}`;

            const cardClasses = [
              'li-card',
              isPrimary  ? 'li-card--primary'    : '',
              selectable ? 'li-card--selectable' : '',
            ].filter(Boolean).join(' ');

            return `
              <div class="${cardClasses}"${selectable ? ` data-location-id="${loc.id}"` : ''}>
                <div class="li-card__header">
                  <span class="li-card__name">${loc.name}</span>
                  <span class="li-card__distance">${dist}</span>
                </div>
                <div class="li-card__address">${loc.address}</div>
                <div class="li-compact li-card__services">${servicesHtml}</div>
              </div>
            `;
          };

          const othersHtml = others.length > 0 ? `
            <details class="li-others">
              <summary class="li-others__toggle">Ver otras sucursales (${others.length})</summary>
              <div class="li-others__panel li-others__panel--scrollable">
                ${others.map((l) => renderCard(l, false, true)).join('')}
              </div>
            </details>
          ` : '';

          this.innerHTML = `
            <div class="li-widget">
              <p class="li-widget__label">${primaryLabel}</p>
              ${renderCard(primary, true, false)}
              ${othersHtml}
            </div>
          `;

          this.querySelectorAll('[data-location-id]').forEach((card) => {
            card.addEventListener('click', () => this._selectLocation(card.dataset.locationId));
          });
        }

        _selectLocation(locationId) {
          try { localStorage.setItem(LI_STORAGE_KEY_LOCATION, String(locationId)); } catch {}
          if (typeof publish === 'function') {
            publish('location-inventory:location-changed', { locationId });
          }
        }

        _renderCompact({ sorted, variantAvailable, selectedId }) {
          if (!sorted.length) {
            this.innerHTML = '';
            return;
          }

          const display          = sorted.find((l) => String(l.id) === String(selectedId)) || sorted[0];
          const canPickUpCompact = display.pickUpEnabled !== false && display.available;

          // If truly nothing to show, render nothing
          if (!canPickUpCompact && !variantAvailable) {
            this.innerHTML = '';
            return;
          }

          const pickupItem = `
            <div class="li-compact__item li-compact__item--${canPickUpCompact ? 'available' : 'unavailable'}">
              <span class="li-compact__icon-box li-compact__icon-box--pickup"></span>
              <span class="li-compact__info">
                <span class="li-compact__name">${display.name}</span>
                <span class="li-compact__status">${canPickUpCompact ? 'Disponible para retiro' : 'Sin stock para retiro'}</span>
              </span>
            </div>`;

          const shippingItem = variantAvailable
            ? `<div class="li-compact__item li-compact__item--available">
                 <span class="li-compact__icon-box li-compact__icon-box--delivery"></span>
                 <span class="li-compact__info">
                   <span class="li-compact__name">Envío a domicilio</span>
                   <span class="li-compact__status">Disponible</span>
                 </span>
               </div>`
            : '';

          this.innerHTML = `<div class="li-compact">${pickupItem}${shippingItem}</div>`;
        }

        _renderExpandable({ sorted, all, variantAvailable, selectedId }) {
          this._lastRenderData = { sorted, all, variantAvailable, selectedId };

          if (!sorted.length) {
            this.innerHTML = '';
            return;
          }

          const display      = sorted.find((l) => String(l.id) === String(selectedId)) || sorted[0];
          const canPickUpExp = display.pickUpEnabled !== false && display.available;

          const pickupHtml = `
            <div class="li-compact__item li-compact__item--${canPickUpExp ? 'available' : 'unavailable'}">
              <span class="li-compact__icon-box li-compact__icon-box--pickup"></span>
              <span class="li-compact__info">
                <span class="li-compact__name">${display.name}</span>
                <span class="li-compact__status">${canPickUpExp ? 'Disponible para retiro' : 'Sin stock para retiro'}</span>
              </span>
            </div>`;

          const shippingHtml = variantAvailable
            ? `<div class="li-compact__item li-compact__item--available">
                 <span class="li-compact__icon-box li-compact__icon-box--delivery"></span>
                 <span class="li-compact__info">
                   <span class="li-compact__name">Envío a domicilio</span>
                   <span class="li-compact__status">Disponible</span>
                 </span>
               </div>`
            : '';

          const rowLocations = all && all.length ? all : sorted;

          const rows = rowLocations.map((loc) => {
            const isSelected  = String(loc.id) === String(selectedId);
            const dLabel      = loc.distanceKm != null
              ? (loc.distanceKm < 1
                  ? `${Math.round(loc.distanceKm * 1000)} m`
                  : `${loc.distanceKm.toFixed(1)} km`)
              : '';
            const canPickUpRow = loc.pickUpEnabled !== false && loc.available;
            const pickupText  = canPickUpRow
              ? (loc.pickUpTime || 'Disponible para retirar')
              : 'Sin stock para retirar';
            const pickupClass = canPickUpRow ? 'li-status--available' : 'li-status--unavailable';

            return `
              <div class="li-location${isSelected ? ' li-location--selected' : ''}" data-location-id="${loc.id}">
                <div class="li-location__header">
                  <span class="li-location__name">${loc.name}</span>
                  <span class="li-location__distance">${isSelected ? '✓ Seleccionada' : (dLabel || 'Seleccionar')}</span>
                </div>
                <div class="li-location__address">${loc.address}</div>
                <div class="li-location__pickup li-status ${pickupClass}">
                  <span class="li-status__icon"></span>
                  <span class="li-status__text">${pickupText}</span>
                </div>
                ${variantAvailable ? `
                  <div class="li-location__shipping li-status li-status--available">
                    <span class="li-status__icon"></span>
                    <span class="li-status__text">Disponible para envío</span>
                  </div>
                ` : ''}
              </div>
            `;
          }).join('');

          this.innerHTML = `
            <div class="li-compact">
              ${pickupHtml}
              ${shippingHtml}
              <button class="li-compact__toggle" aria-expanded="${this._expanded}">
                ${this._expanded ? 'Cerrar' : 'Cambiar sucursal'}
              </button>
              <div class="li-compact__panel${this._expanded ? '' : ' li-compact__panel--hidden'}">
                <div class="li-widget li-widget--inline">${rows}</div>
              </div>
            </div>
          `;

          this.querySelector('.li-compact__toggle').addEventListener('click', () => {
            this._expanded = !this._expanded;
            this._renderExpandable(this._lastRenderData);
          });

          this.querySelectorAll('[data-location-id]').forEach((row) => {
            row.addEventListener('click', () => {
              this._selectLocation(row.dataset.locationId);
              this._expanded = false;
            });
          });
        }

        _renderError() {
          this.innerHTML = `
            <div class="li-widget li-widget--error">
              <p class="li-error__message">No se pudo cargar la disponibilidad por sucursal.</p>
              <button class="li-retry-btn">Reintentar</button>
            </div>
          `;
          this.querySelector('.li-retry-btn')?.addEventListener('click', () => this.refresh());
        }

        _setLoading(isLoading) {
          if (isLoading) {
            this.innerHTML = `
              <div class="li-widget li-widget--loading">
                <span class="li-spinner"></span>
              </div>
            `;
          }
        }

        _getSelectedLocationId() {
          try { return localStorage.getItem(LI_STORAGE_KEY_LOCATION); } catch { return null; }
        }
      }
    );
  }

  window.__LI = { GeoService, haversineDistanceKm };

})();
